/**
 * SpriteGenerator - Procedural sprite generation from creature genome
 * Creates unique pixel art sprites based on creature properties
 */

import { Texture } from 'pixi.js';

export interface CreatureGenomeVisual {
  id: string;
  size: number; // Base size (affects sprite dimensions)
  color: number; // Hue 0-360
}

interface SpriteData {
  width: number;
  height: number;
  pixels: number[];
  palette: string[];
}

// Deterministic random based on string seed
function seededRandom(seed: string): () => number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return () => {
    hash = (hash * 1103515245 + 12345) & 0x7fffffff;
    return hash / 0x7fffffff;
  };
}

// Convert hue to RGB (0-1 range)
function hueToRgb(hue: number): { r: number; g: number; b: number } {
  const h = ((hue % 360) + 360) % 360;
  const s = 0.8; // Saturation
  const l = 0.5; // Lightness

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return { r: r + m, g: g + m, b: b + m };
}

// Convert RGB to hex
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Generate color palette from hue
function generatePalette(hue: number): string[] {
  const { r, g, b } = hueToRgb(hue);

  return [
    'transparent', // 0: Transparent
    rgbToHex(r, g, b), // 1: Primary
    rgbToHex(r * 0.5, g * 0.5, b * 0.5), // 2: Outline/Dark
    rgbToHex(Math.min(1, r + 0.3), Math.min(1, g + 0.3), Math.min(1, b + 0.3)), // 3: Highlight
    '#FFFFFF', // 4: Eye white
    '#111111', // 5: Eye pupil
    rgbToHex(r * 0.7, g * 0.7, b * 0.7), // 6: Mid-tone
  ];
}

// Calculate sprite size from creature size
function calculateSpriteSize(creatureSize: number): number {
  // Map size ~5-20 to pixel sizes
  if (creatureSize <= 8) return 12;
  if (creatureSize <= 12) return 16;
  if (creatureSize <= 16) return 20;
  return 24;
}

// Draw filled circle
function drawCircle(
  pixels: number[],
  size: number,
  centerX: number,
  centerY: number,
  radius: number,
  colorIndex: number
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - centerX + 0.5;
      const dy = y - centerY + 0.5;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radius) {
        pixels[y * size + x] = colorIndex;
      }
    }
  }
}

// Draw outline of circle
function drawCircleOutline(
  pixels: number[],
  size: number,
  centerX: number,
  centerY: number,
  radius: number,
  colorIndex: number
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - centerX + 0.5;
      const dy = y - centerY + 0.5;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radius && dist > radius - 1.5) {
        pixels[y * size + x] = colorIndex;
      }
    }
  }
}

// Draw oval body
function drawOval(
  pixels: number[],
  size: number,
  centerX: number,
  centerY: number,
  radiusX: number,
  radiusY: number,
  colorIndex: number
) {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - centerX + 0.5) / radiusX;
      const dy = (y - centerY + 0.5) / radiusY;

      if (dx * dx + dy * dy <= 1) {
        pixels[y * size + x] = colorIndex;
      }
    }
  }
}

// Draw eye at position
function drawEye(pixels: number[], size: number, x: number, y: number, eyeSize: number) {
  const px = Math.round(x);
  const py = Math.round(y);

  if (px < 0 || px >= size || py < 0 || py >= size) return;

  // Eye white
  if (eyeSize >= 2) {
    drawCircle(pixels, size, px, py, eyeSize, 4);
  } else {
    pixels[py * size + px] = 4;
  }

  // Pupil
  pixels[py * size + px] = 5;
}

// Draw tail
function drawTail(
  pixels: number[],
  size: number,
  startX: number,
  startY: number,
  length: number,
  colorIndex: number,
  _random: () => number
) {
  let x = startX;
  let y = startY;

  for (let i = 0; i < length; i++) {
    if (y >= size) break;

    const px = Math.round(x);
    const py = Math.round(y);

    if (px >= 0 && px < size && py >= 0 && py < size) {
      pixels[py * size + px] = colorIndex;
    }

    // Wavy motion
    x += Math.sin(i * 0.8) * 0.8;
    y += 1;
  }
}

// Draw spikes
function drawSpikes(
  pixels: number[],
  size: number,
  centerX: number,
  centerY: number,
  bodyRadius: number,
  spikeCount: number,
  spikeLength: number,
  colorIndex: number
) {
  for (let i = 0; i < spikeCount; i++) {
    const angle = (i / spikeCount) * Math.PI * 2;
    const startX = centerX + Math.cos(angle) * bodyRadius;
    const startY = centerY + Math.sin(angle) * bodyRadius;

    for (let j = 0; j < spikeLength; j++) {
      const x = Math.round(startX + Math.cos(angle) * j);
      const y = Math.round(startY + Math.sin(angle) * j);

      if (x >= 0 && x < size && y >= 0 && y < size) {
        pixels[y * size + x] = colorIndex;
      }
    }
  }
}

// Add highlight to body
function addHighlight(
  pixels: number[],
  size: number,
  centerX: number,
  centerY: number,
  radius: number
) {
  // Add highlight in upper-left quadrant
  const highlightX = centerX - radius * 0.3;
  const highlightY = centerY - radius * 0.3;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - highlightX;
      const dy = y - highlightY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < radius * 0.4 && pixels[y * size + x] === 1) {
        pixels[y * size + x] = 3; // Highlight color
      }
    }
  }
}

// Generate sprite data from genome
function generateSpriteData(genome: CreatureGenomeVisual): SpriteData {
  const size = calculateSpriteSize(genome.size);
  const pixels = new Array(size * size).fill(0);
  const palette = generatePalette(genome.color);

  // Seeded random for deterministic features
  const random = seededRandom(genome.id);

  const center = size / 2;
  const bodyRadius = size / 2 - 2;

  // Determine features from ID
  const eyeCount = Math.floor(random() * 3) + 1; // 1-3 eyes
  const hasSpikes = random() > 0.6;
  const hasTail = random() > 0.5;
  const bodyShape = random(); // 0-1 for shape variation

  // Draw body based on shape variation
  if (bodyShape < 0.33) {
    // Circle
    drawCircle(pixels, size, center, center, bodyRadius, 1);
  } else if (bodyShape < 0.66) {
    // Horizontal oval
    drawOval(pixels, size, center, center, bodyRadius, bodyRadius * 0.7, 1);
  } else {
    // Vertical oval (elongated)
    drawOval(pixels, size, center, center, bodyRadius * 0.7, bodyRadius, 1);
  }

  // Add outline
  drawCircleOutline(pixels, size, center, center, bodyRadius + 0.5, 2);

  // Add highlight
  addHighlight(pixels, size, center, center, bodyRadius);

  // Add spikes if has them
  if (hasSpikes) {
    const spikeCount = Math.floor(random() * 4) + 3; // 3-6 spikes
    const spikeLength = Math.floor(size / 6) + 1;
    drawSpikes(pixels, size, center, center, bodyRadius - 1, spikeCount, spikeLength, 2);
  }

  // Add tail if has it
  if (hasTail) {
    const tailLength = Math.floor(size / 3);
    drawTail(pixels, size, center, center + bodyRadius - 1, tailLength, 6, random);
  }

  // Add eyes
  const eyeRadius = Math.max(1, Math.floor(size / 10));
  const eyeDistance = bodyRadius * 0.4;

  if (eyeCount === 1) {
    drawEye(pixels, size, center, center - eyeDistance, eyeRadius);
  } else if (eyeCount === 2) {
    const eyeSpacing = bodyRadius * 0.4;
    drawEye(pixels, size, center - eyeSpacing, center - eyeDistance * 0.7, eyeRadius);
    drawEye(pixels, size, center + eyeSpacing, center - eyeDistance * 0.7, eyeRadius);
  } else {
    // 3 eyes in triangle
    drawEye(pixels, size, center, center - eyeDistance, eyeRadius);
    drawEye(pixels, size, center - bodyRadius * 0.35, center, eyeRadius);
    drawEye(pixels, size, center + bodyRadius * 0.35, center, eyeRadius);
  }

  return { width: size, height: size, pixels, palette };
}

// Convert sprite data to canvas
function spriteToCanvas(sprite: SpriteData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = sprite.width;
  canvas.height = sprite.height;
  const ctx = canvas.getContext('2d')!;

  // Clear with transparency
  ctx.clearRect(0, 0, sprite.width, sprite.height);

  sprite.pixels.forEach((colorIndex, i) => {
    if (colorIndex === 0) return; // Skip transparent
    const x = i % sprite.width;
    const y = Math.floor(i / sprite.width);
    ctx.fillStyle = sprite.palette[colorIndex];
    ctx.fillRect(x, y, 1, 1);
  });

  return canvas;
}

/**
 * SpriteGenerator class with caching
 */
export class SpriteGenerator {
  private cache = new Map<string, Texture>();
  private maxCacheSize = 500;

  /**
   * Generate or retrieve cached texture for a creature
   */
  generate(genome: CreatureGenomeVisual): Texture {
    // Create cache key from visual properties
    const cacheKey = this.getCacheKey(genome);

    // Check cache
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // Generate new sprite
    const spriteData = generateSpriteData(genome);
    const canvas = spriteToCanvas(spriteData);
    const texture = Texture.from(canvas);

    // Cache management
    if (this.cache.size >= this.maxCacheSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        const oldTexture = this.cache.get(firstKey);
        oldTexture?.destroy();
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(cacheKey, texture);
    return texture;
  }

  /**
   * Generate cache key from genome
   * Similar genomes (same size range and color) share sprites
   */
  private getCacheKey(genome: CreatureGenomeVisual): string {
    // Quantize size to reduce unique sprites
    const sizeKey = calculateSpriteSize(genome.size);
    // Quantize color to 36 buckets (every 10 degrees)
    const colorKey = Math.floor(genome.color / 10) * 10;
    // Use ID prefix for unique features
    const idKey = genome.id.slice(0, 8);

    return `${sizeKey}-${colorKey}-${idKey}`;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    for (const texture of this.cache.values()) {
      texture.destroy();
    }
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; maxSize: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
    };
  }
}

// Singleton instance
let spriteGeneratorInstance: SpriteGenerator | null = null;

export function getSpriteGenerator(): SpriteGenerator {
  if (!spriteGeneratorInstance) {
    spriteGeneratorInstance = new SpriteGenerator();
  }
  return spriteGeneratorInstance;
}
